import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      remove: (widgetId: string) => void
    }
  }
}

const scriptId = 'lobework-turnstile-script'

function ensureScript() {
  if (document.getElementById(scriptId)) return
  const script = document.createElement('script')
  script.id = scriptId
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const siteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim()
  const host = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | null>(null)
  const [ready, setReady] = useState(Boolean(window.turnstile))

  useEffect(() => {
    if (!siteKey) return
    ensureScript()
    if (window.turnstile) setReady(true)
    const timer = window.setInterval(() => {
      if (window.turnstile) {
        setReady(true)
        window.clearInterval(timer)
      }
    }, 100)
    return () => window.clearInterval(timer)
  }, [siteKey])

  useEffect(() => {
    if (!siteKey || !ready || !host.current || !window.turnstile || widgetId.current) return
    widgetId.current = window.turnstile.render(host.current, {
      sitekey: siteKey,
      theme: 'light',
      callback: (token: string) => onToken(token),
      'expired-callback': () => onToken(''),
      'error-callback': () => onToken('')
    })
    return () => {
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current)
      widgetId.current = null
    }
  }, [siteKey, ready, onToken])

  if (!siteKey) {
    return <div className="turnstile-unavailable">Security verification is not configured for this deployment yet.</div>
  }

  return <div className="turnstile-host" ref={host} />
}
